import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Search, Edit, Trash2, Truck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const initialFormData = {
  codigo: "",
  nombre: "",
  identificacion_fiscal: "",
  persona_contacto: "",
  telefono: "",
  telefono_otro: "",
  email: "",
  email_otro: "",
  direccion: "",
  pais: "",
  comentarios: "",
};

export default function Logistics() {
  const [logistics, setLogistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const { hasRole } = useAuth();

  useEffect(() => {
    fetchLogistics();
  }, []);

  const fetchLogistics = async () => {
    try {
      const response = await api.get("/logistics");
      setLogistics(response.data);
    } catch (error) {
      toast.error("Error al cargar los operadores logísticos");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (logistic = null) => {
    if (logistic) {
      setEditingId(logistic.id);
      setFormData({
        codigo: logistic.codigo || "",
        nombre: logistic.nombre || "",
        identificacion_fiscal: logistic.identificacion_fiscal || "",
        persona_contacto: logistic.persona_contacto || "",
        telefono: logistic.telefono || "",
        telefono_otro: logistic.telefono_otro || "",
        email: logistic.email || "",
        email_otro: logistic.email_otro || "",
        direccion: logistic.direccion || "",
        pais: logistic.pais || "",
        comentarios: logistic.comentarios || "",
      });
    } else {
      setEditingId(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre) {
      toast.error("El nombre del operador es obligatorio");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/logistics/${editingId}`, formData);
        toast.success("Operador actualizado correctamente");
      } else {
        await api.post("/logistics", formData);
        toast.success("Operador creado correctamente");
      }
      fetchLogistics();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el operador");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/logistics/${deleteId}`);
      toast.success("Operador eliminado correctamente");
      setLogistics(logistics.filter((l) => l.id !== deleteId));
    } catch (error) {
      toast.error("Error al eliminar el operador");
    } finally {
      setDeleteId(null);
    }
  };

  const filteredLogistics = logistics.filter(
    (logistic) =>
      logistic.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      logistic.email?.toLowerCase().includes(search.toLowerCase()) ||
      logistic.identificacion_fiscal?.toLowerCase().includes(search.toLowerCase()) ||
      logistic.codigo?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="logistics-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Operadores Logísticos</h1>
          <p className="text-slate-500 mt-1">Gestión de operadores de transporte y logística</p>
        </div>
        {hasRole("admin") && (
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="create-logistics-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Operador
          </Button>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre, email, CIF o código..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-logistics"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : filteredLogistics.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-sm border border-slate-200">
          <Truck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No hay operadores logísticos</h3>
          <p className="text-slate-500 mb-4">
            {search ? "No se encontraron operadores con ese criterio" : "Comienza creando tu primer operador"}
          </p>
          {hasRole("admin") && !search && (
            <Button onClick={() => handleOpenDialog()} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Crear Operador
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Código
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Nombre
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  CIF
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Contacto
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Email
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  País
                </TableHead>
                {hasRole("admin") && (
                  <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider w-[100px]">
                    Acciones
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogistics.map((logistic) => (
                <TableRow key={logistic.id} className="hover:bg-slate-50" data-testid={`logistics-row-${logistic.id}`}>
                  <TableCell className="font-mono text-slate-500">{logistic.codigo || "-"}</TableCell>
                  <TableCell className="font-medium">{logistic.nombre}</TableCell>
                  <TableCell className="font-mono">{logistic.identificacion_fiscal || "-"}</TableCell>
                  <TableCell>{logistic.persona_contacto || "-"}</TableCell>
                  <TableCell className="text-slate-500">{logistic.email || "-"}</TableCell>
                  <TableCell>{logistic.pais || "-"}</TableCell>
                  {hasRole("admin") && (
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenDialog(logistic)}
                          data-testid={`edit-logistics-${logistic.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 hover:text-red-700"
                          onClick={() => setDeleteId(logistic.id)}
                          data-testid={`delete-logistics-${logistic.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Operador" : "Nuevo Operador"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Código (ID)</Label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                  placeholder="Ej: L01"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Nombre de la empresa"
                  required
                  data-testid="input-logistics-nombre"
                />
              </div>
              <div className="space-y-2">
                <Label>Identificación Fiscal (CIF)</Label>
                <Input
                  value={formData.identificacion_fiscal}
                  onChange={(e) => setFormData({ ...formData, identificacion_fiscal: e.target.value })}
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>País</Label>
                <Input
                  value={formData.pais}
                  onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Persona de Contacto</Label>
                <Input
                  value={formData.persona_contacto}
                  onChange={(e) => setFormData({ ...formData, persona_contacto: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono Principal</Label>
                <Input
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Otro Teléfono</Label>
                <Input
                  value={formData.telefono_otro}
                  onChange={(e) => setFormData({ ...formData, telefono_otro: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email Principal</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contacto@empresa.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Otro Email</Label>
                <Input
                  type="email"
                  value={formData.email_otro}
                  onChange={(e) => setFormData({ ...formData, email_otro: e.target.value })}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Dirección</Label>
                <Input
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  placeholder="Dirección completa"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Comentarios</Label>
                <Textarea
                  value={formData.comentarios}
                  onChange={(e) => setFormData({ ...formData, comentarios: e.target.value })}
                  placeholder="Notas adicionales..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={saving} data-testid="save-logistics-button">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : editingId ? (
                  "Actualizar"
                ) : (
                  "Crear"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar operador?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el operador.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
